import dayjs from 'dayjs';
import { CapacityRange } from '../models/serializable/capacity-range.js';
import { CalendarDaySlot } from '../models/serializable/calendar-state/calendar-day-slot.js';
import { CalendarDayState } from '../models/serializable/calendar-state/calendar-day-state.js';
import { GroupReservationCalendarSlot } from '../models/serializable/calendar-state/group-reservation-calendar-slot.js';
import { OpenReservationCalendarSlot } from '../models/serializable/calendar-state/open-reservation-calendar-slot.js';
import { SingleEntryCalendarSlot } from '../models/serializable/calendar-state/single-entry-calendar-slot.js';
import { TimeOfDay } from '../models/serializable/time-of-day.js';
import { DatabaseIdGenerator } from '../utility/database-id-generator.js';
import { SerializationException } from '../exceptions/serialization-exception.js';
import { BinaryReader } from './binary-reader.js';
import { BinaryWriter } from './binary-writer.js';

export class LegacyCalendarStateSerializer {
  private static readonly SINGLE_ENTRY_SLOT_ID: number = 1;
  private static readonly GROUP_ENTRY_SLOT_ID: number = 2;
  private static readonly OPEN_ENTRY_SLOT_ID: number = 3;

  static serialize(calendars: Map<string, CalendarDayState>): Uint8Array {
    return this.asBytes_v3(calendars);
  }

  static deserialize(bytes: Uint8Array): Map<string, CalendarDayState> {
    const reader = BinaryReader.fromBytes(bytes);
    const format = reader.readByte();

    switch(format) {
      case 3:
      case 2:
        return this.fromBytes_v3(reader);
      case 1:
        return this.fromBytes_v1(reader);
    }

    throw new SerializationException(`Calendar state cannot be deserialized because format ${format} is unknown`);
  }

  // BEGINNING OF: FORMAT VERSION 3
  private static asBytes_v3(calendars: Map<string, CalendarDayState>): Uint8Array {
    const writer = new BinaryWriter();
    const format = this.isBackwardsCompatible_v2(calendars) ? 2 : 3;

    writer.writeByte(format);
    writer.writeByte(calendars.size);

    for(const calendarState of calendars.values()) {
      this.writeCalendarState_v3(writer, calendarState);
    }

    return writer.toBytes();
  }

  static isBackwardsCompatible_v2(calendars: Map<string, CalendarDayState>): boolean {
    for(const calendarState of calendars.values()) {
      for(const slot of calendarState.slots) {
        const isCompatible = slot instanceof SingleEntryCalendarSlot || slot instanceof GroupReservationCalendarSlot;
        if(!isCompatible) {
          return false;
        }
      }
    }

    return true;
  }

  static writeCalendarState_v3(writer: BinaryWriter, calendarState: CalendarDayState): void {
    if(calendarState.uniqueId.length > DatabaseIdGenerator.kAuthIdLength) {
      throw new SerializationException('Calendar day state ID is not valid');
    }

    this.writeUniqueId(writer, calendarState.uniqueId, DatabaseIdGenerator.kAuthIdLength);

    const slots = calendarState.slots;
    writer.writeInt32(slots.length);

    for(let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if(slot.entryId.length > DatabaseIdGenerator.kAutoIdLength) {
        throw new SerializationException('Calendar entry id is not valid');
      }

      if(slot instanceof SingleEntryCalendarSlot) {
        this.writeSingleEntryCalendarSlot_v3(writer, slot);
      }
      else if(slot instanceof GroupReservationCalendarSlot) {
        this.writeGroupCalendarSlot_v3(writer, slot);
      }
      else if(slot instanceof OpenReservationCalendarSlot) {
        this.writeOpenCalendarSlot_v3(writer, slot);
      }
      else {
        throw new SerializationException(`Unsupported staff calendar slot: ${slot.constructor.name}`);
      }
    }
  }

  static writeSingleEntryCalendarSlot_v3(writer: BinaryWriter, slot: SingleEntryCalendarSlot) {
    writer.writeByte(this.SINGLE_ENTRY_SLOT_ID);

    this.writeUniqueId(writer, slot.entryId, DatabaseIdGenerator.kAutoIdLength);
    writer.writeInt32(slot.timeOfDay.inMinutes);
    writer.writeInt32(slot.duration.asMilliseconds());
  }

  static writeGroupCalendarSlot_v3(writer: BinaryWriter, slot: GroupReservationCalendarSlot) {
    writer.writeByte(this.GROUP_ENTRY_SLOT_ID);

    this.writeUniqueId(writer, slot.entryId, DatabaseIdGenerator.kAutoIdLength);
    this.writeUniqueId(writer, slot.serviceId, DatabaseIdGenerator.kAutoIdLength);

    writer.writeInt32(slot.timeOfDay.inMinutes);
    writer.writeInt32(slot.duration.asMilliseconds());
    writer.writeInt32(slot.capacity.from);
    writer.writeInt32(slot.capacity.to);
    writer.writeInt32(slot.occupancy);
  }

  static writeOpenCalendarSlot_v3(writer: BinaryWriter, slot: OpenReservationCalendarSlot) {
    writer.writeByte(this.OPEN_ENTRY_SLOT_ID);

    this.writeUniqueId(writer, slot.entryId, DatabaseIdGenerator.kAutoIdLength);
    this.writeUniqueId(writer, slot.serviceId, DatabaseIdGenerator.kAutoIdLength);

    writer.writeInt32(slot.timeOfDay.inMinutes);
    writer.writeInt32(slot.duration.asMilliseconds());
    writer.writeBool(slot.isOpen);
  }

  private static fromBytes_v3(reader: BinaryReader): Map<string, CalendarDayState> {
    const calendars = new Map<string, CalendarDayState>();
    const stateCount = reader.readByte();

    for(let i = 0; i < stateCount; i++) {
      const calendarState = this.readCalendarState_v3(reader);
      calendars.set(calendarState.uniqueId, calendarState);
    }

    return calendars;
  }

  static readCalendarState_v3(reader: BinaryReader): CalendarDayState {
    const uniqueId = this.readUniqueId(reader, DatabaseIdGenerator.kAuthIdLength);
    const slots: CalendarDaySlot[] = [];
    const numberOfSlots = reader.readInt32();

    for(let i = 0; i < numberOfSlots; i++) {
      const slotType = reader.readByte();

      if(slotType === this.SINGLE_ENTRY_SLOT_ID) {
        slots.push(this.readSingleEntryCalendarSlot_v3(reader));
      }
      else if(slotType === this.GROUP_ENTRY_SLOT_ID) {
        slots.push(this.readGroupCalendarSlot_v3(reader));
      }
      else if(slotType === this.OPEN_ENTRY_SLOT_ID) {
        slots.push(this.readOpenCalendarSlot_v3(reader));
      }
      else {
        throw new SerializationException(`Unknown staff calendar slot type: ${slotType}`);
      }
    }

    return new CalendarDayState(uniqueId, slots);
  }

  static readSingleEntryCalendarSlot_v3(reader: BinaryReader): CalendarDaySlot {
    const entryId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);
    const timeOfDay = TimeOfDay.fromMinutes(reader.readInt32());
    const duration = dayjs.duration(reader.readInt32());

    return new SingleEntryCalendarSlot(entryId, timeOfDay, duration);
  }

  static readGroupCalendarSlot_v3(reader: BinaryReader): CalendarDaySlot {
    const entryId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);
    const serviceId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);

    const timeOfDay = TimeOfDay.fromMinutes(reader.readInt32());
    const duration = dayjs.duration(reader.readInt32());
    const capacity = CapacityRange.between(reader.readInt32(), reader.readInt32());
    const occupancy = reader.readInt32();

    return new GroupReservationCalendarSlot(entryId, timeOfDay, duration, serviceId, capacity, occupancy);
  }

  static readOpenCalendarSlot_v3(reader: BinaryReader): CalendarDaySlot {
    const entryId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);
    const serviceId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);

    const timeOfDay = TimeOfDay.fromMinutes(reader.readInt32());
    const duration = dayjs.duration(reader.readInt32());
    const isOpen = reader.readBool();

    return new OpenReservationCalendarSlot(entryId, timeOfDay, duration, serviceId, isOpen);
  }
  // END OF: FORMAT VERSION 3

  // BEGINNING OF: FORMAT VERSION 1
  private static fromBytes_v1(reader: BinaryReader): Map<string, CalendarDayState> {
    const calendars = new Map<string, CalendarDayState>();
    const stateCount = reader.readByte();

    for(let i = 0; i < stateCount; i++) {
      const calendarState = this.readCalendarState_v1(reader);
      calendars.set(calendarState.uniqueId, calendarState);
    }

    return calendars;
  }

  static readCalendarState_v1(reader: BinaryReader): CalendarDayState {
    const uniqueId = this.readUniqueId(reader, DatabaseIdGenerator.kAuthIdLength);
    const numberOfIntervals = reader.readInt32();
    const calendarState = new Map<number, string[]>();

    for(let i = 0; i < numberOfIntervals; i++) {
      const slots: string[] = [];
      const key = reader.readInt32();
      const count = reader.readByte();

      calendarState.set(key, slots);
      for(let c = 0; c < count; c++) {
        slots.push(this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength));
      }
    }

    return CalendarDayState.fromLegacyCalendarState(uniqueId, calendarState);
  }
  // END OF: FORMAT VERSION 1

  static writeUniqueId(writer: BinaryWriter, uniqueId: string, maxLength: number) {
    let length = 0;

    for(let i = 0; i < uniqueId.length; i++) {
      writer.writeByte(uniqueId.charCodeAt(i));
      length++;
    }

    while(length < maxLength) {
      writer.writeByte(0);
      length++;
    }
  }

  static readUniqueId(reader: BinaryReader, maxLength: number): string {
    const charCodes: number[] = [];
    for(let i = 0; i < maxLength; i++) {
      const code = reader.readByte();
      if(code > 0) {
        charCodes.push(code);
      }
    }

    return String.fromCharCode(...charCodes);
  }
}
